/**
 * Helpers shared by the video quiz builder (contract: docs/video-quiz-contract.md).
 */
import type {
  QuizQuestion, VideoConfig, VideoPart, VideoStyle, VideoStyleProp,
} from '../types/quizzes';
import { VIDEO_QUESTION_TYPES } from '../types/quizzes';

export function defaultVideoConfig(): VideoConfig {
  return {
    driver: 'native',
    mainEnd: 0,
    segments: [],
    enforcement: { preventSkip: true },
    leadTiming: 'end',
    incompleteTagIds: [],
  };
}

export function defaultVideoStyle(): VideoStyle {
  return {
    theme: {
      fontFamily: 'Inter',
      primaryColor: '#4F46E5',
      textColor: '#111827',
      backgroundColor: '#000000',
      cardBackground: '#FFFFFF',
      borderRadius: '12px',
      spacing: '16px',
      maxWidth: '960px',
    },
    components: {},
    customCss: '',
  };
}

export function isVideoQuestionType(t: QuizQuestion['type']): boolean {
  return (VIDEO_QUESTION_TYPES as readonly string[]).includes(t);
}

/** Seconds → `m:ss.s` */
export function formatTime(sec: number | undefined | null): string {
  if (sec === undefined || sec === null || !Number.isFinite(sec)) return '';
  const s = Math.max(0, sec);
  const m = Math.floor(s / 60);
  const rest = s - m * 60;
  const whole = Math.floor(rest);
  const tenth = Math.floor((rest - whole) * 10 + 1e-6);
  return `${m}:${String(whole).padStart(2, '0')}.${tenth}`;
}

/** Accepts `m:ss.s`, `h:mm:ss`, or plain seconds. Returns null when unparseable. */
export function parseTime(input: string): number | null {
  const v = input.trim();
  if (!v) return null;
  const parts = v.split(':');
  if (parts.length > 3) return null;
  let total = 0;
  for (const p of parts) {
    if (!/^\d+(\.\d+)?$/.test(p)) return null;
    total = total * 60 + parseFloat(p);
  }
  return Math.round(total * 10) / 10;
}

/** Round to a tenth of a second (the precision shown in the builder). */
export function roundTime(sec: number): number {
  return Math.round(sec * 10) / 10;
}

/** Ids must match ^[A-Za-z0-9_-]{1,64}$ — a UUID does. */
export function newId(): string {
  return crypto.randomUUID();
}

/** Questions a viewer answers but whose answers would vanish after the sync (no attribute, no answer tags). */
export function unmappedQuestions(questions: QuizQuestion[]): QuizQuestion[] {
  return questions.filter((q) => {
    if (q.attributeKey && q.attributeKey.trim()) return false;
    if (q.type !== 'text_input' && q.answers.some((a) => (a.tagIds?.length ?? 0) > 0)) return false;
    return true;
  });
}

/**
 * Questions in play order: timed questions by stop time (timed ones missing a time next),
 * then follow-up questions, each group in sortOrder.
 */
export function sortByAt(questions: QuizQuestion[]): QuizQuestion[] {
  const rank = (q: QuizQuestion) => (q.followUp ? 2 : q.at === undefined || q.at === null ? 1 : 0);
  return [...questions].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    if (ra === 0 && a.at !== b.at) return (a.at as number) - (b.at as number);
    return a.sortOrder - b.sortOrder;
  });
}

export const isFollowUp = (q: QuizQuestion): boolean => q.followUp === true;

/**
 * Turn a question into a follow-up (drops its stop time) or back into a timed one
 * (gets a stop time 5 s after the last timed question, and answers that asked it go back to "Continue").
 */
export function setFollowUp(questions: QuizQuestion[], qId: string, on: boolean): QuizQuestion[] {
  if (on) {
    return questions.map((q) => {
      if (q.id !== qId) return q;
      const { at: _at, ...rest } = q;
      void _at;
      return { ...rest, followUp: true as const };
    });
  }
  const lastAt = questions.reduce((m, x) => (x.followUp ? m : Math.max(m, x.at ?? 0)), 0);
  return questions.map((q) => {
    if (q.id === qId) {
      const { followUp: _f, ...rest } = q;
      void _f;
      return { ...rest, at: roundTime(lastAt + 5) };
    }
    return clearAsksTo(q, qId);
  });
}

/** Answers that ask `targetId` go back to "Continue". */
export function clearAsksTo(q: QuizQuestion, targetId: string): QuizQuestion {
  if (!q.answers.some((a) => a.next?.type === 'ask' && a.next.questionId === targetId)) return q;
  return {
    ...q,
    answers: q.answers.map((a) => (a.next?.type === 'ask' && a.next.questionId === targetId ? { ...a, next: { type: 'continue' as const } } : a)),
  };
}

/** Follow-up questions that no answer asks (they'd never be shown). */
export function orphanFollowUps(questions: QuizQuestion[]): QuizQuestion[] {
  const asked = new Set<string>();
  for (const q of questions) for (const a of q.answers) if (a.next?.type === 'ask') asked.add(a.next.questionId);
  return questions.filter((q) => q.followUp && !asked.has(q.id));
}

/** Titles of follow-up chains that loop back on themselves (the server rejects these). */
export function followUpCycles(questions: QuizQuestion[]): string[][] {
  const byId = new Map(questions.map((q) => [q.id, q]));
  const edges = (q: QuizQuestion) =>
    Array.from(new Set(q.answers.flatMap((a) => (a.next?.type === 'ask' ? [a.next.questionId] : [])))).filter((id) => byId.has(id));
  const state = new Map<string, 1 | 2>(); // 1 = on stack, 2 = done
  const stack: string[] = [];
  const cycles: string[][] = [];
  const seen = new Set<string>();
  const visit = (id: string) => {
    state.set(id, 1);
    stack.push(id);
    for (const next of edges(byId.get(id)!)) {
      const st = state.get(next);
      if (st === 1) {
        const loop = stack.slice(stack.indexOf(next));
        const key = [...loop].sort().join('|');
        if (!seen.has(key)) {
          seen.add(key);
          cycles.push([...loop, next].map((x) => byId.get(x)?.title || 'Untitled question'));
        }
      } else if (!st) visit(next);
    }
    stack.pop();
    state.set(id, 2);
  };
  for (const q of questions) if (!state.has(q.id)) visit(q.id);
  return cycles;
}

const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * Pull a Voomly video id out of whatever the user pasted: the bare id, Voomly's embed code
 * (`<div class="voomly-embed" data-id="…">`), or a share/watch URL (last path segment).
 * Returns null when nothing usable is found.
 */
export function extractVoomlyId(input: string): string | null {
  const v = input.trim();
  if (!v) return null;
  if (ID_RE.test(v)) return v;
  const attr = v.match(/data-id\s*=\s*["']?([A-Za-z0-9_-]{1,64})/i);
  if (attr) return attr[1];
  const urlMatch = v.match(/https?:\/\/[^\s"'<>]+/i)?.[0] ?? (/^[\w.-]+\.[a-z]{2,}\/\S+$/i.test(v) ? `https://${v}` : null);
  if (urlMatch) {
    try {
      const u = new URL(urlMatch);
      const fromQuery = u.searchParams.get('id') ?? u.searchParams.get('v');
      if (fromQuery && ID_RE.test(fromQuery)) return fromQuery;
      const segs = u.pathname.split('/').filter(Boolean);
      for (let i = segs.length - 1; i >= 0; i--) {
        if (ID_RE.test(segs[i]) && !['v', 'embed', 'watch', 'video', 'videos', 'share'].includes(segs[i].toLowerCase())) return segs[i];
      }
    } catch { /* not a URL */ }
  }
  return null;
}

export const isValidVoomlyId = (id: string | undefined): boolean => !!id && ID_RE.test(id);

export const CAPTION_MAX = 100;
export const CAPTION_TEXT_MAX = 200;

export function sortCaptions<T extends { from: number }>(captions: T[]): T[] {
  return [...captions].sort((a, b) => a.from - b.from);
}

/** Problems worth flagging in the builder before the server rejects the save. */
export function videoWarnings(video: VideoConfig, questions: QuizQuestion[]): string[] {
  const out: string[] = [];
  if (video.driver === 'voomly') {
    if (!video.voomlyId) out.push('No Voomly video yet — enter its video ID.');
    else if (!isValidVoomlyId(video.voomlyId)) out.push('The Voomly video ID looks wrong — paste the data-id from Voomly\'s embed code.');
    if (!(video.mainEnd > 0)) out.push('Set where the main video ends ("Main video ends at").');
  } else {
    if (!video.mediaKey && !video.url) out.push('No video yet — upload one or paste an https URL.');
    if (video.url && !/^https:\/\//i.test(video.url)) out.push('The video URL must start with https://');
    if (!(video.mainEnd > 0)) out.push('Set where the main video ends ("Set main video end here").');
  }
  const seen = new Map<number, string>();
  for (const q of questions) {
    if (!isVideoQuestionType(q.type)) out.push(`"${q.title}" uses a question type video quizzes don't support.`);
    if (q.followUp) continue;
    if (q.at === undefined || q.at === null) { out.push(`"${q.title}" has no stop time.`); continue; }
    if (video.mainEnd > 0 && (q.at <= 0 || q.at >= video.mainEnd)) out.push(`"${q.title}" stops at ${formatTime(q.at)}, outside the main video (0 – ${formatTime(video.mainEnd)}).`);
    const dup = seen.get(q.at);
    if (dup) out.push(`"${q.title}" and "${dup}" stop at the same time.`);
    seen.set(q.at, q.title);
  }
  for (const q of orphanFollowUps(questions)) out.push(`Follow-up "${q.title}" is never asked — pick it in an answer's "Then" menu.`);
  for (const loop of followUpCycles(questions)) out.push(`Follow-ups loop back on themselves: ${loop.map((t) => `"${t}"`).join(' → ')}.`);
  for (const q of questions) for (const a of q.answers) {
    if (a.next?.type !== 'ask') continue;
    const target = a.next.questionId;
    if (!questions.some((x) => x.id === target && x.followUp)) out.push(`An answer in "${q.title}" asks a follow-up that no longer exists.`);
  }
  const caps = video.captions ?? [];
  if (caps.length > CAPTION_MAX) out.push(`Too many captions (${caps.length}) — the limit is ${CAPTION_MAX}.`);
  for (const c of caps) {
    if (!(c.to > c.from)) out.push(`Caption "${c.text.slice(0, 40) || 'empty'}" ends before it starts.`);
    if (!c.text.trim()) out.push(`A caption at ${formatTime(c.from)} has no text.`);
    if (c.text.length > CAPTION_TEXT_MAX) out.push(`Caption at ${formatTime(c.from)} is longer than ${CAPTION_TEXT_MAX} characters.`);
  }
  for (const s of video.segments) {
    if (!(s.end > s.start)) out.push(`Clip "${s.label}" ends before it starts.`);
    else if (video.mainEnd > 0 && s.start < video.mainEnd) out.push(`Clip "${s.label}" starts before the main video ends — branch clips must live after it.`);
  }
  return out;
}

/** Make an existing quiz's questions usable in a video quiz. */
export function adaptQuestionsForVideo(questions: QuizQuestion[]): QuizQuestion[] {
  const sorted = [...questions].sort((a, b) => a.sortOrder - b.sortOrder);
  return sorted.map((q, i) => ({
    ...q,
    type: isVideoQuestionType(q.type) ? q.type : 'single_choice',
    at: q.at ?? (i + 1) * 5,
  }));
}

export const VIDEO_PARTS: Array<{ value: VideoPart; label: string; group: string }> = [
  { value: 'root', label: 'Whole quiz', group: 'Frame' },
  { value: 'player', label: 'Player frame', group: 'Frame' },
  { value: 'video', label: 'Video', group: 'Frame' },
  { value: 'play-button', label: 'Play button', group: 'Frame' },
  { value: 'progress', label: 'Progress track', group: 'Frame' },
  { value: 'progress-bar', label: 'Progress bar fill', group: 'Frame' },
  { value: 'caption', label: 'Captions', group: 'Frame' },
  { value: 'fullscreen-button', label: 'Fullscreen button', group: 'Frame' },
  { value: 'overlay', label: 'Question backdrop', group: 'Question' },
  { value: 'question-card', label: 'Question card', group: 'Question' },
  { value: 'question-title', label: 'Question title', group: 'Question' },
  { value: 'question-description', label: 'Question description', group: 'Question' },
  { value: 'answers', label: 'Answer list', group: 'Question' },
  { value: 'answer-button', label: 'Answer button', group: 'Question' },
  { value: 'answer-button-selected', label: 'Answer button (selected)', group: 'Question' },
  { value: 'checkbox', label: 'Checkbox (multi-select)', group: 'Question' },
  { value: 'text-answer', label: 'Text answer box', group: 'Question' },
  { value: 'continue-button', label: 'Continue / submit button', group: 'Question' },
  { value: 'lead-form', label: 'Lead form', group: 'Lead form' },
  { value: 'lead-label', label: 'Lead form labels', group: 'Lead form' },
  { value: 'lead-input', label: 'Lead form inputs', group: 'Lead form' },
  { value: 'lead-button', label: 'Lead form button', group: 'Lead form' },
  { value: 'lead-error', label: 'Lead form error text', group: 'Lead form' },
  { value: 'resume-card', label: 'Resume card', group: 'Resume & end' },
  { value: 'resume-button', label: 'Resume button', group: 'Resume & end' },
  { value: 'end-card', label: 'End / thank-you card', group: 'Resume & end' },
  { value: 'end-title', label: 'End card title', group: 'Resume & end' },
  { value: 'end-text', label: 'End card text', group: 'Resume & end' },
  { value: 'end-button', label: 'End card button', group: 'Resume & end' },
  { value: 'error', label: 'Error message', group: 'Resume & end' },
];

export const STYLE_PROPS: Array<{ value: VideoStyleProp; label: string; kind: 'color' | 'text' | 'select'; placeholder?: string; options?: string[] }> = [
  { value: 'color', label: 'Text color', kind: 'color' },
  { value: 'backgroundColor', label: 'Background color', kind: 'color' },
  { value: 'background', label: 'Background', kind: 'text', placeholder: '#fff or linear-gradient(…)' },
  { value: 'borderColor', label: 'Border color', kind: 'color' },
  { value: 'borderWidth', label: 'Border width', kind: 'text', placeholder: '1px' },
  { value: 'borderStyle', label: 'Border style', kind: 'select', options: ['solid', 'dashed', 'dotted', 'none'] },
  { value: 'borderRadius', label: 'Corner radius', kind: 'text', placeholder: '12px' },
  { value: 'fontFamily', label: 'Font', kind: 'text', placeholder: 'Inter, sans-serif' },
  { value: 'fontSize', label: 'Font size', kind: 'text', placeholder: '16px' },
  { value: 'fontWeight', label: 'Font weight', kind: 'select', options: ['300', '400', '500', '600', '700', '800'] },
  { value: 'textAlign', label: 'Text align', kind: 'select', options: ['left', 'center', 'right'] },
  { value: 'padding', label: 'Padding', kind: 'text', placeholder: '12px 16px' },
  { value: 'margin', label: 'Margin', kind: 'text', placeholder: '0 0 8px' },
  { value: 'gap', label: 'Gap', kind: 'text', placeholder: '8px' },
  { value: 'boxShadow', label: 'Shadow', kind: 'text', placeholder: '0 4px 12px rgba(0,0,0,.2)' },
  { value: 'width', label: 'Width', kind: 'text', placeholder: '100%' },
  { value: 'maxWidth', label: 'Max width', kind: 'text', placeholder: '640px' },
  { value: 'height', label: 'Height', kind: 'text', placeholder: 'auto' },
  { value: 'opacity', label: 'Opacity', kind: 'text', placeholder: '0.9' },
];

export const VIDEO_FONT_OPTIONS = ['Inter', 'Roboto', 'Open Sans', 'Lato', 'Poppins', 'Montserrat', 'Arial', 'Georgia', 'system-ui'];
